import { useState } from "react";
import { createContainer } from "unstated-next";
import { MygruBackendService } from "../services/MygruBackendService";
import { StampCardService } from "../services/StampCardService";
import { StampService } from "../services/StampService";
import { Stamp } from "./Stamps";

type StampCard = {
    id: string;
    contactCode: string;
    campaignId: string;
    stamps?: AttachedStamp[];
    recommends?: { [key: string]: Recommend[] };
    selectedRecommends: { category: string, recommend: Recommend }[];
    isEntry: boolean;
    isLatest: boolean;
    questionnaireStatus: QuestionnaireStatus;
}

export type Recommend = {
    id: string,
    image: string,
    name: string,
    isAchieve?: boolean,
    isChecked?: boolean
    banner?: string,
    genre?: string,
    area?: string[],
    areaNumber?: number[],
    budget?: string,
    businessHours?: string,
    tel?: string,
    description?: string,
    others?: string
}

type AttachedStamp = {
    category: string,
    isAchieve: boolean,
    isChecked: boolean,
} & Stamp;

type QuestionnaireStatus = {
    isAnswered: boolean;
    answers?: QuestionnaireAnswer[]
}

type QuestionnaireAnswer = {
    qId: string,
    answers: number[]
}

/**
 * スタンプカードコンテナ
 */
const useStampCardContainer = () => {
    const [stampCard, setStampCard] = useState<StampCard | null>(null);

    /**
     * スタンプカードデータを初期化する
     * @param userId ユーザーID
     * @param campaignId キャンペーンID
     * @param apiToken トークン
     */
    const initStampCard = async (userId: string, campaignId: string, apiToken: string) => {
        let isLatest = false;
        let stampCardData = await StampCardService().getStampCard(userId, campaignId);
        if (!stampCardData.data?.isEntry) {
            // 未エントリーの場合、初期化APIを呼び出しデータを再取得
            await MygruBackendService(apiToken).initStampCard(userId, campaignId);
            stampCardData = await StampCardService().getStampCard(userId, campaignId);
        } else if (stampCardData.data.isEntry && stampCardData.data.stamps) {
            // エントリー済みの場合、スタンプの達成状態を更新
            await MygruBackendService(apiToken).updateStampCard(userId, campaignId);
            stampCardData = await StampCardService().getStampCard(userId, campaignId);
            isLatest = true;
        }

        if (!stampCardData.data) {
            throw new Error(`Failed initStampCard. userId: ${userId} campaignId: ${campaignId}`);
        }

        let stamps: AttachedStamp[] | undefined = undefined;
        if (stampCardData.data.stamps) {
            // スタンプのデータを取得 (並列非同期)
            stamps = await Promise.all<AttachedStamp>(
                stampCardData.data.stamps.map(async (s: any): Promise<AttachedStamp> => {
                    const stampData = await StampService().getStamp(campaignId, s.stamp.id);
                    return {
                        id: stampData.id,
                        image: stampData.data.image,
                        name: stampData.data.displayName,
                        category: stampData.data.category,
                        isAchieve: s.isAchieve,
                        isChecked: s.isChecked,
                        area: stampData.data.area,
                        areaNumber: stampData.data.areaNumber,
                        banner: stampData.data.banner,
                        genre: stampData.data.genre,
                        budget: stampData.data.badget,
                        businessHours: stampData.data.businessHours,
                        tel: stampData.data.tel,
                        description: stampData.data.description,
                        others: stampData.data.others
                    }
                }));
        }

        const recommendsData = stampCardData.data.recommends;
        let recommends: { [key: string]: Recommend[] } | undefined = undefined;
        if (recommendsData) {
            // リコメンドデータを取得 (並列非同期)
            const recommendTemp = await Promise.all<{ [key: string]: Recommend[] }>(
                Object.keys(recommendsData).map(async (key: string): Promise<{ [key: string]: Recommend[] }> => {
                    const recommendStamps = await Promise.all<Recommend>(recommendsData[key].map(async (s: any): Promise<Recommend> => {
                        const stampData = await StampService().getStamp(campaignId, s.id);
                        return {
                            id: stampData.id,
                            image: stampData.data.image,
                            name: stampData.data.displayName,
                            banner: stampData.data.banner || "",
                            genre: stampData.data.genre,
                            area: stampData.data.area,
                            budget: stampData.data.budget,
                            businessHours: stampData.data.businessHours,
                            tel: stampData.data.tel,
                            description: stampData.data.description,
                            others: stampData.data.others,
                        };
                    }));

                    return { [key]: recommendStamps };
                }));
            recommends = Object.assign({}, ...recommendTemp);
        }

        // スタンプ変更時に選択中スタンプに現在のスタンプを表示するため
        // エントリー済みの場合 selectedRecommends にスタンプを格納する。
        let selectedRecommends: { category: string, recommend: Recommend }[] = []
        if (stampCardData.data.isEntry && stamps) {
            selectedRecommends = stamps.map((s) => {
                return {
                    category: s.category,
                    recommend:
                    {
                        id: s.id,
                        image: s.image,
                        name: s.name,
                        isAchieve: s.isAchieve,
                        isChecked: s.isChecked
                    }
                }
            }
            )
        }

        const stampCard = {
            id: stampCardData.id,
            contactCode: stampCardData.data.contactCode,
            campaignId: campaignId,
            stamps: stamps,
            recommends: recommends,
            selectedRecommends: selectedRecommends,
            isEntry: stampCardData.data.isEntry,
            isLatest: isLatest,
            questionnaireStatus: {
                isAnswered: stampCardData.data.questionnaireAnswers !== undefined,
                answers: stampCardData.data.questionnaireAnswers || null
            }
        }

        setStampCard({ ...stampCard });
    };

    /**
     * スタンプカードのエントリー状態を更新する
     * @param campaignFrom キャンペーン開始日
     * @param campaignTo キャンペーン終了日
     * @returns 更新結果 ok: 成功 | preparing: キャンペーン開始前 | end: キャンペーン終了後
     */
    const entry = async (campaignFrom: Date, campaignTo: Date) => {
        if (!stampCard) {
            return;
        }

        // エントリー状態を更新
        const result = await StampCardService().entry(stampCard.campaignId, stampCard.id, campaignFrom, campaignTo);

        if (result === "ok") {
            // 更新に成功した場合、Stateも更新する
            stampCard.isEntry = true;
            setStampCard({ ...stampCard });
        }

        return result;
    };

    /**
     * スタンプカードのリコメンドを取得する
     * @param userId ユーザーID
     * @param campaignId キャンペーンID
     * @param categories リコメンドを取得するカテゴリ
     * @param apiToken トークン
     */
    const recommend = async (userId: string, campaignId: string, categories: string[], apiToken: string) => {
        // リコメンドAPIを呼び出し、最新のスタンプカードで初期化する
        if (stampCard) {
            // 既存のリコメンドをリセット
            stampCard.recommends = undefined;
            setStampCard({ ...stampCard });
        }

        await MygruBackendService(apiToken).recommendStampCard(userId, campaignId, categories);
        await initStampCard(userId, campaignId, apiToken);
    };

    /**
     * リコメンドされたスタンプの選択
     * @param category カテゴリ
     * @param selectedRecommend 選択されたリコメンド
     */
    const recommendSelect = (category: string, selectedRecommend: Recommend) => {
        if (!stampCard?.recommends) {
            return stampCard;
        }

        if (stampCard.selectedRecommends.some(r => r.category === category)) {
            // 既に同一カテゴリのスタンプが選択されている場合はそのスタンプを取り外す
            stampCard.selectedRecommends = stampCard.selectedRecommends.filter(sr => sr.category !== category);
        }

        // 選ばれたスタンプを選択済みスタンプに格納
        stampCard.selectedRecommends.push({ category, recommend: selectedRecommend });

        setStampCard({ ...stampCard });
    };

    /**
     * リコメンド選択中のスタンプを取り外す
     * @param id スタンプID
     */
    const removeSelectedRecommend = (id: string) => {
        if (!stampCard) {
            return stampCard;
        }

        stampCard.selectedRecommends = stampCard.selectedRecommends.filter(sr => sr.recommend.id !== id);

        setStampCard({ ...stampCard });
    };

    /**
     * 選択されたスタンプ結果を設定
     * @param apiToken apiトークン
     * @param isCcmabUpdate CCMABモデルを更新するか
     */
    const setStamps = async (apiToken: string, isCcmabUpdate?: boolean) => {
        if (!stampCard) {
            return;
        }

        const selectedStampIds: { category: string, stampId: string }[] = stampCard.selectedRecommends.map(sr => {
            return { category: sr.category, stampId: sr.recommend.id, isAchieve: sr.recommend.isAchieve, isCecked: sr.recommend.isChecked };
        });

        await MygruBackendService(apiToken).setStamps(stampCard.campaignId, stampCard.id, selectedStampIds, isCcmabUpdate);

        stampCard.stamps = stampCard.selectedRecommends.map(sr => {
            return {
                id: sr.recommend.id,
                image: sr.recommend.image,
                name: sr.recommend.name,
                category: sr.category,
                isAchieve: sr.recommend.isAchieve || false,
                isChecked: sr.recommend.isChecked || false,
                area: sr.recommend.area || [],
                areaNumber: sr.recommend.areaNumber || [],
            }
        });

        stampCard.isLatest = false;

        setStampCard({ ...stampCard });
    };

    /**
     * アンケートの回答リストを初期化する
     */
    const initQuestionnaireAnswers = (qIds: string[]) => {
        if (!stampCard) {
            return;
        }
        stampCard.questionnaireStatus.answers = [];
        setStampCard({ ...stampCard });
    }

    /**
     * アンケートの回答を更新する
     * @param qId 設問ID
     * @param answer 回答番号
     * @param multiple 複数選択の可否
     */
    const updateAnswers = (qId: string, answer: number, multiple: boolean) => {
        if (!stampCard || !stampCard.questionnaireStatus.answers) {
            return;
        }

        const existingIdx = stampCard.questionnaireStatus.answers.findIndex((q) => q.qId === qId);
        let ans: QuestionnaireAnswer = { qId: qId, answers: [] };
        if (existingIdx !== -1) {
            ans = stampCard.questionnaireStatus.answers[existingIdx];
        }

        if (multiple) {
            // 複数選択問題の場合
            if (ans?.answers.includes(answer)) {
                ans.answers = ans.answers.filter(a => a !== answer);
            } else {
                ans.answers.push(answer);
                ans.answers.sort();
            }
        } else {
            // 単一選択問題の場合
            ans.answers = [answer];
        }

        if (existingIdx === -1) {
            stampCard.questionnaireStatus.answers.push(ans);
        } else {
            stampCard.questionnaireStatus.answers.splice(existingIdx, 1, ans)
        }
        setStampCard({ ...stampCard });
    }

    /**
     * アンケートの回答を Firestore に登録する
     */
    const setAnswers = async () => {
        if (!stampCard || !stampCard.questionnaireStatus.answers) {
            return;
        }
        const answers = stampCard.questionnaireStatus.answers;
        await StampCardService().setAnswers(stampCard.campaignId, stampCard.id, answers);
        stampCard.questionnaireStatus.isAnswered = true;
        setStampCard({ ...stampCard });
    }

    /**
     * スタンプカードのクリア
     */
    const resetStampCard = () => {
        setStampCard(null);
    };

    return {
        stampCard,
        initStampCard,
        entry,
        recommend,
        recommendSelect,
        removeSelectedRecommend,
        setStamps,
        initQuestionnaireAnswers,
        updateAnswers,
        setAnswers,
        resetStampCard
    };
};

export const StampCardContainer = createContainer(useStampCardContainer);
